import dynamic from "next/dynamic";
import { GetServerSideProps, GetServerSidePropsContext } from "next/types";
import { useEffect, useState } from "react";

import { getAnalytics } from "@tm/insurance/analytics";
import { navigationActions } from "@tm/insurance/navigation";
import {
  getCookieByName,
  getDeviceIdForServer,
  getRandomBgImage,
  getThemePartner,
  resolveAndPersistInternalSource,
} from "@tm/insurance/utils";
import { PARTNER } from "@tm/isomorphic-utils";
import {
  getCommonLandingPageDetails,
  getCommonsHeaderData,
  getLandingPageDetails,
} from "@tm/server-api-utils";
import { getPartnerOnServerSide } from "@tm/server-utils";
import { PartnerContextProvider } from "@tm/static/partnerContext";
import { withDeviceAndThemeProvider } from "@tm/ui-widgets";
import {
  withExperimentTrackingStringProvider,
  withReferrerProvider,
} from "@tm/ui-widgets/hoc";

import { resolveCmpForApiFromQuery } from "@tm/api-utils";
import { trackEvent } from "@tm/insurance/analytics/xdm";
import {
  InsuranceCommonLandingPageMainWrapper,
  InsuranceGiCommonLandingPageMainWrapper,
  InsuranceGiLandingPageMainWrapper,
  InsuranceLandingMyBizPageMainWrapper,
  InsuranceLandingMyPartnerPageMainWrapper,
} from "@tm/static/pages";
import { withError } from "@tm/ui/hoc";
import { initGlobalErrorLogging } from "@tm/utility/analytics";
import React from "react";
import { withSeoHeadTags } from "../../src/hoc";
import {
  authenticatedGSSP,
  AuthenticatedGSSPProps,
} from "../../src/utils/authenticatedGSSP";
import { conditionallyLoadWebVitals } from "../../src/utils/webVitals";

// Conditional dynamic imports with SSR enabled - only load what's needed based on device
// Using webpack magic comments for better code splitting
const InsuranceLanding = dynamic(
  () =>
    import(
      /* webpackChunkName: "insurance-landing-mobile" */
      "@tm/static/pages"
    ).then((mod) => mod.InsuranceLanding),
  { ssr: true }
);

const InsuranceCommonLanding = dynamic(
  () =>
    import(
      /* webpackChunkName: "insurance-common-landing-desktop" */
      "@tm/static/pages"
    ).then((mod) => mod.InsuranceCommonLanding),
  { ssr: true }
);

// Lazy load header HOC modules for code splitting
// Create wrapper components that use the HOCs internally
// This enables proper code splitting while maintaining SSR
const GIHeaderWrapper = dynamic(
  () =>
    import(
      /* webpackChunkName: "gi-insurance-header" */
      "@tm/ui/hoc"
    ).then((mod) => {
      const { withGIInsuranceHeader } = mod;
      return function GIHeaderWrapperComponent({
        children,
        headerOptions,
        mobileTitle,
        myOrders,
        goToMyOrders,
        displayWhiteHeader,
        showPwaHeaderBackButton,
        onLoginSuccess,
      }: {
        children: React.ReactNode;
        headerOptions?: any;
        mobileTitle?: string;
        myOrders?: boolean;
        goToMyOrders?: () => void;
        displayWhiteHeader?: boolean;
        showPwaHeaderBackButton?: boolean;
        onLoginSuccess?: () => void;
      }) {
        return withGIInsuranceHeader(
          <>{children}</>,
          headerOptions,
          mobileTitle,
          myOrders,
          goToMyOrders,
          displayWhiteHeader,
          showPwaHeaderBackButton,
          onLoginSuccess
        );
      };
    }),
  { ssr: true }
);

const MMTHeaderWrapper = dynamic(
  () =>
    import(
      /* webpackChunkName: "mmt-insurance-header" */
      "@tm/ui/hoc"
    ).then((mod) => {
      const { withMMTInsuranceHeader } = mod;
      return function MMTHeaderWrapperComponent({
        children,
        headerOptions,
        mobileTitle,
        myOrders,
        goToMyOrders,
        displayWhiteHeader,
        isMyBiz,
        b2bLayoutData,
        exitWebViewOnBack,
        showPwaHeaderBackButton,
        showPwaHeader,
      }: {
        children: React.ReactNode;
        headerOptions?: any;
        mobileTitle?: string;
        myOrders?: boolean;
        goToMyOrders?: () => void;
        displayWhiteHeader?: boolean;
        isMyBiz?: boolean;
        exitWebViewOnBack?: boolean;
        showPwaHeaderBackButton?: boolean;
        showPwaHeader?: boolean;
        b2bLayoutData?: any;
      }) {
        return withMMTInsuranceHeader(
          <>{children}</>,
          headerOptions,
          mobileTitle,
          myOrders,
          goToMyOrders,
          displayWhiteHeader,
          isMyBiz,
          exitWebViewOnBack,
          showPwaHeaderBackButton,
          showPwaHeader,
          b2bLayoutData
        );
      };
    }),
  { ssr: true }
);

const MyPartnerHeaderWrapper = dynamic(
  () =>
    import(
      /* webpackChunkName: "mypartner-insurance-header" */
      "@tm/ui/hoc"
    ).then((mod) => {
      const { withMyPartnerInsuranceHeader } = mod;
      return function MyPartnerHeaderWrapperComponent({
        children,
        headerOptions,
        b2bLayoutData,
        mobileTitle,
        myOrders,
      }: {
        children: React.ReactNode;
        headerOptions?: any;
        mobileTitle?: string;
        b2bLayoutData?: any;
        myOrders?: boolean;
      }) {
        return withMyPartnerInsuranceHeader(
          <>{children}</>,
          headerOptions,
          mobileTitle,
          b2bLayoutData,
          myOrders
        );
      };
    }),
  { ssr: true }
);

const analytics = getAnalytics();

export function CommonLandingPage({
  data,
  device = "mobile",
  error,
  // hasPartnerAuth,
  partner,
  referrer,
  internalSource = "",
  experiment = "",
  query,
  commonsHeaderData,
  bgImageIndex,
  isMobileDevice,
  isTrackCookies,
}: AuthenticatedGSSPProps) {
  const headTags = data?.headTags || {};
  const longImageUrl = data?.offers?.items?.[0]?.image_url;
  const [activeModal, setActiveModal] = useState("");
  const [randomImage, setRandomImage] = useState<string | number>(
    getRandomBgImage(bgImageIndex)
  );

  useEffect(() => {
    const cleanup = initGlobalErrorLogging("seo_insurance_landing");
    return cleanup;
  }, []);

  useEffect(() => {
    conditionallyLoadWebVitals();
  }, []);

  useEffect(() => {
    if (partner === PARTNER.MMT) {
      document.cookie = `isCommonLanding=true; domain=.makemytrip.com; path=/`;
    }

    resolveAndPersistInternalSource(internalSource, {
      hostnameRules: [
        {
          hostnames: [
            "www.makemytrip.com",
            "www.goibibo.com",
            "mybiz.makemytrip.com",
            "mypartner.makemytrip.com",
            "insurance.makemytrip.com",
          ],
          value: "secondary_icon",
        },
        { hostnames: ["www.google.com"], value: "seo" },
      ],
    });

    // Set random image on client side only
    const bgImage = getRandomBgImage(bgImageIndex);
    setRandomImage(bgImage);
  }, [bgImageIndex, internalSource, partner]);

  const goToMyOrders = () => {
    analytics?.trackLandingEvents(
      {
        actionType: "my_orders_clicked",
      }
      //'click'
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "my_orders_clicked",
      },
    });

    const authCookie =
      getCookieByName("mmt-auth") || getCookieByName("OAUTH-GOIBIBO");

    if (authCookie) {
      navigationActions.navigateToOrderListing();
    } else {
      setActiveModal?.("LOGIN");
    }
  };
  const onModalChange = (modal: string) => {
    if (modal === "LOGIN" || modal === "") {
      setActiveModal(modal);
    }
  };

  //TODO: Create a global context and move common data things there

  const landingContent = isMobileDevice ? (
    <InsuranceLanding
      setActiveModal={setActiveModal}
      data={data}
      query={query}
    />
  ) : (
    <InsuranceCommonLanding
      setActiveModal={setActiveModal}
      data={data}
      query={query}
      partnerType={partner}
    />
  );

  const comp =
    partner === PARTNER.GI ? (
      <PartnerContextProvider partnerType={PARTNER.GI}>
        <GIHeaderWrapper
          headerOptions={{
            Wrapper: (props: any) =>
              isMobileDevice ? (
                <InsuranceGiLandingPageMainWrapper
                  id="common-landing-main-wrapper"
                  {...props}
                />
              ) : (
                <InsuranceGiCommonLandingPageMainWrapper
                  id="common-landing-main-wrapper"
                  {...props}
                  style={{ backgroundColor: "#f4f4f4" }}
                />
              ),
            forceLogin: false,
          }}
          mobileTitle="Secure International Travel"
          myOrders={true}
          goToMyOrders={goToMyOrders}
        >
          {landingContent}
        </GIHeaderWrapper>
      </PartnerContextProvider>
    ) : partner === PARTNER.MYPARTNER ? (
      <MyPartnerHeaderWrapper
        headerOptions={{
          type: "dualHeader",
          Wrapper: InsuranceLandingMyPartnerPageMainWrapper,
          forceLogin: false,
          ...(data?.myPartnerHorizontalStripeData && {
            commonsData: {
              myPartnerHorizontalStripeData:
                data.myPartnerHorizontalStripeData,
            },
          }),
          pokusExperiments: {
            mypStandaloneInsurance: true,
            mypartnerMima: true,
            mypVisaCommon: data?.myPartnerStandaloneVisaEnabled || false,
            mypHolidaysCommon: data?.myPartnerHolidaysCommonsEnabled || false,
          },
          globalConfig: commonsHeaderData?.headerConfig?.value,
          headerCommonData: {
            loyaltyHeaderData: commonsHeaderData?.loyaltyHeaderData?.value,
            walletHeaderData: commonsHeaderData?.walletHeaderData?.value,
            mobileCore: commonsHeaderData?.mobileCore?.value,
            userInfoData: commonsHeaderData?.userInfoData?.value,
          },
        }}
        mobileTitle="Secure International Travel"
        b2bLayoutData={commonsHeaderData?.b2bLayoutData?.value}
        myOrders={false}
      >
        <PartnerContextProvider partnerType={PARTNER.MMT}>
          {landingContent}
        </PartnerContextProvider>
      </MyPartnerHeaderWrapper>
    ) : (
      <PartnerContextProvider partnerType={partner}>
        <MMTHeaderWrapper
          headerOptions={{
            type: "dualHeader",
            Wrapper: (props: any) =>
              partner === PARTNER.MYBIZ ? (
                <InsuranceLandingMyBizPageMainWrapper
                  style={{
                    backgroundImage:
                      "linear-gradient(178.99deg, #ffcbcb -28%, rgba(255, 255, 255, 0) 57.04%)",
                    backgroundSize: "100% 990px",
                    backgroundRepeat: "no-repeat",
                    backgroundColor: "#f4f4f4",
                    paddingTop: "5px",
                    position: "relative",
                    zIndex: 2,
                  }}
                  id="common-landing-main-wrapper"
                  {...props}
                >
                  {props.children}
                </InsuranceLandingMyBizPageMainWrapper>
              ) : (
                <InsuranceCommonLandingPageMainWrapper
                  style={{
                    backgroundImage: `url(${randomImage}), linear-gradient(to bottom, #051322, #15457c)`,
                    backgroundSize: "100% 643px",
                  }}
                  id="common-landing-main-wrapper"
                  isCommonLandingRevamp={
                    data?.pokusExperiments?.isCommonLandingRevamp
                  }
                  {...props}
                >
                  {/* Hidden img tag to make LCP image discoverable in HTML with fetchpriority */}
                  {typeof randomImage === "string" && !isMobileDevice && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={randomImage}
                      alt=""
                      style={{
                        position: "absolute",
                        width: 0,
                        height: 0,
                        opacity: 0,
                        pointerEvents: "none",
                      }}
                      {...({ fetchpriority: "high" } as any)}
                      loading="eager"
                    />
                  )}
                  {props.children}
                </InsuranceCommonLandingPageMainWrapper>
              ),
            activeModal: activeModal || "",
            onModalChange: onModalChange,
            forceLogin: false,
            pokusExperiments: {
              insuranceNewFunnel: true,
            },
            globalConfig: commonsHeaderData?.headerConfig?.value,
            headerCommonData: {
              loyaltyHeaderData: commonsHeaderData?.loyaltyHeaderData?.value,
              walletHeaderData: commonsHeaderData?.walletHeaderData?.value,
              mobileCore: commonsHeaderData?.mobileCore?.value,
              userInfoData: commonsHeaderData?.userInfoData?.value,
            },
          }}
          mobileTitle="Insurance & Protection Plans"
          myOrders={true}
          goToMyOrders={goToMyOrders}
          displayWhiteHeader={false}
          isMyBiz={false}
          b2bLayoutData={commonsHeaderData?.b2bLayoutData?.value}
          exitWebViewOnBack={false}
          showPwaHeaderBackButton={true}
          showPwaHeader={isMobileDevice}
        >
          {landingContent}
        </MMTHeaderWrapper>
      </PartnerContextProvider>
    );

  return withError(
    withReferrerProvider(
      withDeviceAndThemeProvider(
        withSeoHeadTags(
          withExperimentTrackingStringProvider(comp, experiment),
          headTags,
          partner,
          longImageUrl,
          data?.seoFaq || data?.faq
        ),
        device,
        { pageTheme: getThemePartner(partner) }
      ),
      referrer
    ),
    error,
    "COMMON_LANDING"
  );
}

export const getServerSideProps: GetServerSideProps = async (
  ctx: GetServerSidePropsContext
) => {
  const { req, query, res } = ctx;

  const partner = getPartnerOnServerSide(req?.headers);
  const dvid = getDeviceIdForServer(req, res, partner);

  const resolvedCmp = resolveCmpForApiFromQuery(query);
  const requestData = {
    partner,
    headers: req.headers,
    cmp: resolvedCmp || "fallback",
    query,
    dvid,
  };
  let commonsHeaderData = null;
  if (partner !== PARTNER.GI) {
    const profile =
      partner === PARTNER.MYBIZ
        ? "BUSINESS"
        : partner === PARTNER.MYPARTNER
        ? "MY_PARTNER"
        : "PERSONAL";
    commonsHeaderData = await getCommonsHeaderData(
      dvid,
      profile,
      req?.cookies?.["mmt-auth"]
    );
  }

  return authenticatedGSSP(
    ctx,
    ({ isMobileDevice }) => {
      if (isMobileDevice) {
        return getLandingPageDetails(requestData);
      }
      return getCommonLandingPageDetails(requestData);
    },
    partner,
    resolvedCmp,
    "common-landing",
    commonsHeaderData
  );
};

export default CommonLandingPage;
